import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { MOCK_ORDERS } from '../../lib/mockData';
import { sendEmail, emailTemplates } from '../../lib/emailService';
import DesignLibrary from './DesignLibrary';
import IconLibrary from './IconLibrary';
import CategoryLibrary from './CategoryLibrary';
import OrderDrawer from './OrderDrawer';
import CustomerList from './CustomerList';
import CustomerDetailView from './CustomerDetailView';
import DeliverySettingsPanel from './DeliverySettingsPanel';

const LOGO_URL = "https://smilevase.com/images/smilevase-logo.png";

const ORDERED_STATUSES = [
  'pending',
  'paid',
  'out_for_delivery',
  'delivered',
  'cancelled',
];

const STATUS_LABELS = {
  pending:          'Order Received',
  paid:             'Payment Confirmed',
  out_for_delivery: 'Out for Delivery',
  delivered:        'Hand Delivered',
  cancelled:        'Cancelled',
};

const STATUS_COLORS = {
  pending:          'bg-yellow-100 text-yellow-700',
  paid:             'bg-green-100 text-green-700',
  out_for_delivery: 'bg-teal-100 text-teal-700',
  delivered:        'bg-sage-100 text-sage-700',
  cancelled:        'bg-red-100 text-red-600',
};

const STATUS_FILTER_COLORS = {
  all:              'bg-charcoal text-white',
  pending:          'bg-yellow-100 text-yellow-700 border border-yellow-200',
  paid:             'bg-green-100 text-green-700 border border-green-200',
  out_for_delivery: 'bg-teal-100 text-teal-700 border border-teal-200',
  delivered:        'bg-sage-100 text-sage-700 border border-sage-200',
  cancelled:        'bg-red-100 text-red-600 border border-red-200',
};

export default function AdminPanel() {
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('orders');
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchAllOrders();
  }, []);

  async function fetchAllOrders() {
    setLoading(true);
    if (isSupabaseConfigured()) {
      const { data, error } = await supabase
        .from('orders')
        .select('*, order_items(*)')
        .order('created_at', { ascending: false });

      if (error) {
        setOrders(MOCK_ORDERS);
      } else if (data) {
        setOrders(data);
        if (selectedOrder) {
          const updated = data.find(o => o.id === selectedOrder.id);
          if (updated) setSelectedOrder(updated);
        }
      }
    } else {
      setOrders(MOCK_ORDERS);
    }
    setLoading(false);
  }

  const updateStatus = async (orderId, newStatus) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;

    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));

    if (isSupabaseConfigured()) {
      try {
        await supabase.from('orders').update({ status: newStatus }).eq('id', orderId);

        const { data: freshOrder } = await supabase
          .from('orders')
          .select('*')
          .eq('id', orderId)
          .single();

        const customerEmail = freshOrder?.customer_email;
          if (customerEmail && newStatus === 'delivered') {
            try {
              await sendEmail(
                customerEmail,
                "Your roses have been delivered — SmileVase",
                emailTemplates.orderDelivered(freshOrder)
              );
            } catch (emailErr) {
              console.error("Delivery notification failed:", emailErr);
            }
          }
      } catch (err) {
        console.error("Failed to update status in database:", err);
        alert("Failed to save status update. Reverting change.");
        fetchAllOrders();
      }
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order? This cannot be undone.')) return;
    setDeletingId(orderId);
    try {
      await supabase.from('orders').delete().eq('id', orderId);
      setOrders(prev => prev.filter(o => o.id !== orderId));
      if (selectedOrder?.id === orderId) {
        setIsDrawerOpen(false);
        setSelectedOrder(null);
      }
    } catch (err) {
      alert('Failed to delete order.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleExportCSV = () => {
    const rows = filteredOrders.map(order => {
      const item = order.order_items?.[0] || {};
      return {
        'Order ID': order.id.substring(0, 8),
        'Customer Name': order.customer_name || '',
        'Customer Email': order.customer_email || '',
        'Customer Phone': order.customer_phone || '',
        'Recipient Name': order.recipient_name || '',
        'Recipient Phone': order.recipient_phone || '',
        'Delivery Address': order.delivery_address || '',
        'Delivery City': order.delivery_city || '',
        'Delivery ZIP': order.delivery_zip || '',
        'Delivery Date': order.delivery_date || '',
        'Design': item.design_name || 'Custom',
        'Size': item.size || '',
        'Color': item.color_preference || '',
        'Price': item.price || '',
        'Status': STATUS_LABELS[order.status] || order.status,
        'Special Instructions': order.special_instructions || '',
        'Order Date': new Date(order.created_at).toLocaleDateString(),
      };
    });

    const headers = Object.keys(rows[0]);
    const csvContent = [
      headers.join(','),
      ...rows.map(row =>
        headers.map(h => `"${String(row[h]).replace(/"/g, '""')}"`).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `smilevase-orders-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleRowClick = (order) => {
    setSelectedOrder(order);
    setIsDrawerOpen(true);
  };

  const handleCustomerSelect = (customer) => {
    // Close drawer if open before navigating to customer detail
    setIsDrawerOpen(false);
    setSelectedOrder(null);
    setViewingCustomer(customer);
  };

  // Stats
  const today = new Date().toISOString().split('T')[0];
  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    deliveringToday: orders.filter(o => o.delivery_date === today).length,
    outForDelivery: orders.filter(o => o.status === 'out_for_delivery').length,
  };

  // Filtering
  const filteredOrders = orders.filter(order => {
    const matchesSearch =
      order.id.toLowerCase().includes(orderSearchTerm.toLowerCase()) ||
      order.customer_name?.toLowerCase().includes(orderSearchTerm.toLowerCase()) ||
      order.customer_email?.toLowerCase().includes(orderSearchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="w-full px-6 md:px-10 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-6">
        <div className="flex items-center gap-4">
          <img src={LOGO_URL} alt="Logo" className="h-16 w-auto" />
          <div>
            <h1 className="text-3xl md:text-4xl font-serif text-charcoal mb-1">Admin Dashboard</h1>
            <p className="text-sage-500 text-sm md:text-lg font-medium">Managing SmileVase platform.</p>
          </div>
        </div>
        <div className="bg-white px-5 py-3 rounded-full shadow-sm border border-sage-100 flex items-center gap-3 text-sm text-sage-600 font-semibold min-h-[44px]">
          <SafeIcon name="Calendar" className="text-blush-300" />
          <span>{new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-8 p-1.5 bg-sage-50/50 rounded-full w-fit overflow-x-auto scrollbar-hide border border-sage-100/50">
        {[
          { id: 'orders', label: 'Orders', icon: 'ShoppingBag' },
          { id: 'customers', label: 'Customers', icon: 'Users' },
          { id: 'designs', label: 'Designs', icon: 'Image' },
          { id: 'icons', label: 'Cards', icon: 'Smile' },
          { id: 'categories', label: 'Categories', icon: 'Tag' },
          { id: 'delivery_settings', label: 'Delivery Settings', icon: 'Truck' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              setViewingCustomer(null);
            }}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold transition-all whitespace-nowrap min-h-[44px] ${
              activeTab === tab.id
                ? 'bg-white text-blush-400 shadow-sm border border-blush-100'
                : 'text-sage-400 hover:text-sage-600'
            }`}
          >
            <SafeIcon name={tab.icon} />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'orders' && (
        <div className="space-y-6">

          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total Orders', value: stats.total, icon: 'ShoppingBag', color: 'text-charcoal' },
              { label: 'Pending Review', value: stats.pending, icon: 'Clock', color: 'text-yellow-600' },
              { label: 'Awaiting Payment', value: stats.awaitingPayment, icon: 'CreditCard', color: 'text-orange-500' },
              { label: 'Delivering Today', value: stats.deliveringToday, icon: 'Truck', color: 'text-teal-600' },
            ].map(stat => (
              <div key={stat.label} className="bg-white rounded-[2rem] p-6 border border-sage-100 shadow-soft flex items-center gap-4">
                <div className="w-12 h-12 bg-sage-50 rounded-2xl flex items-center justify-center shrink-0">
                  <SafeIcon name={stat.icon} className={`text-xl ${stat.color}`} />
                </div>
                <div>
                  <p className="text-2xl font-serif font-bold text-charcoal">{stat.value}</p>
                  <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest leading-tight">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Search + Status Filters + Export */}
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search by name, email or order ID..."
                  className="input-field !pl-12 !rounded-full bg-white"
                  value={orderSearchTerm}
                  onChange={(e) => setOrderSearchTerm(e.target.value)}
                />
                <SafeIcon name="Search" className="absolute left-5 top-1/2 -translate-y-1/2 text-sage-300 text-xl" />
              </div>
              <button
                onClick={handleExportCSV}
                disabled={filteredOrders.length === 0}
                className="btn-secondary whitespace-nowrap flex items-center gap-2"
              >
                <SafeIcon name="Download" /> Export CSV
              </button>
            </div>

            {/* Status filter pills */}
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-4 py-2 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all ${
                  statusFilter === 'all' ? STATUS_FILTER_COLORS.all : 'bg-white text-sage-400 border border-sage-100 hover:border-sage-200'
                }`}
              >
                All ({orders.length})
              </button>
              {ORDERED_STATUSES.map(status => {
                const count = orders.filter(o => o.status === status).length;
                if (count === 0) return null;
                return (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={`px-4 py-2 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all ${
                      statusFilter === status
                        ? STATUS_FILTER_COLORS[status]
                        : 'bg-white text-sage-400 border border-sage-100 hover:border-sage-200'
                    }`}
                  >
                    {STATUS_LABELS[status]} ({count})
                  </button>
                );
              })}
            </div>
          </div>

          {/* Orders Table */}
          <div className="bg-white rounded-[2.5rem] shadow-soft border border-sage-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[800px]">
                <thead>
                  <tr className="bg-sage-50 border-b border-sage-100 text-[10px] text-sage-600 uppercase tracking-widest font-bold">
                    <th className="p-6">Order</th>
                    <th className="p-6">Design</th>
                    <th className="p-6">Customer</th>
                    <th className="p-6">Delivery</th>
                    <th className="p-6">Status</th>
                    <th className="p-6 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-sage-100">
                  {filteredOrders.map(order => (
                    <tr key={order.id} onClick={() => handleRowClick(order)} className="hover:bg-warmwhite transition-colors cursor-pointer group">
                      <td className="p-6">
                        <span className="font-bold text-charcoal block text-sm group-hover:text-blush-500">#{order.id.substring(0, 8)}</span>
                        <span className="text-[10px] text-sage-400 font-medium">{new Date(order.created_at).toLocaleDateString()}</span>
                      </td>
                      <td className="p-6 text-xs text-charcoal font-medium">{order.order_items?.[0]?.design_name || 'Custom'}</td>
                      <td className="p-6">
                        <span className="text-charcoal font-semibold block text-sm">{order.customer_name}</span>
                        <span className="text-[10px] text-sage-400 font-bold">{order.customer_email}</span>
                      </td>
                      <td className="p-6">
                        <span className={`text-sm font-bold block ${order.delivery_date === today ? 'text-teal-600' : 'text-charcoal'}`}>
                          {new Date(order.delivery_date).toLocaleDateString()}
                        </span>
                        {order.delivery_type === 'next_day' && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-600 uppercase tracking-wider">
                            ⚡ Next Day
                          </span>
                        )}
                        {order.delivery_date === today && order.delivery_type !== 'next_day' && (
                          <span className="text-[10px] font-bold text-teal-500 uppercase tracking-wider">Today</span>
                        )}
                      </td>
                      <td className="p-6">
                        <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${STATUS_COLORS[order.status]}`}>
                          {STATUS_LABELS[order.status]}
                        </span>
                      </td>
                      <td className="p-6 text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          <select
                            className="text-xs border border-sage-200 rounded-full px-4 py-2 bg-white font-bold min-h-[36px] outline-none focus:border-blush-200"
                            value={order.status}
                            onChange={(e) => updateStatus(order.id, e.target.value)}
                          >
                            {ORDERED_STATUSES.map(status => (
                              <option key={status} value={status}>{STATUS_LABELS[status]}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleDeleteOrder(order.id)}
                            disabled={deletingId === order.id}
                            className="w-9 h-9 flex items-center justify-center rounded-full border border-red-100 bg-red-50 text-red-400 hover:bg-red-100 transition-colors"
                            title="Delete order"
                          >
                            <SafeIcon name="Trash2" className="text-sm" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {loading && <div className="p-12 text-center text-sage-400 font-bold uppercase tracking-widest text-xs">Loading orders...</div>}
            {!loading && filteredOrders.length === 0 && (
              <div className="p-12 text-center text-sage-400 italic">
                {statusFilter !== 'all' ? `No orders with status "${STATUS_LABELS[statusFilter]}"` : 'No orders found.'}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'customers' && (
        viewingCustomer ? (
          <CustomerDetailView customer={viewingCustomer} onBack={() => setViewingCustomer(null)} onSelectOrder={handleRowClick} />
        ) : (
          <CustomerList onSelectCustomer={handleCustomerSelect} />
        )
      )}

      {activeTab === 'designs' && <DesignLibrary />}
      {activeTab === 'icons' && <IconLibrary />}
      {activeTab === 'categories' && <CategoryLibrary />}
      {activeTab === 'delivery_settings' && <DeliverySettingsPanel />}
      <OrderDrawer
        order={selectedOrder}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onUpdate={fetchAllOrders}
      />
    </div>
  );
}
